import { Component, Input, OnChanges, SimpleChanges, inject } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import {
  BehaviorSubject,
  first,
  forkJoin,
  map,
  Observable,
  of,
  switchMap,
  tap,
} from 'rxjs';
import { ConfirmModalComponent } from 'src/app/core/components/confirm-modal/confirm-modal.component';
import { Constants } from 'src/app/core/constants';
import { AssignmentCommentsFormModel } from 'src/app/core/models/project/assignment/comment/assignment-comments-form';
import { AssignmentComment } from 'src/app/core/models/project/assignment/comment/assignment-comment.interface';
import { AssignmentCommentGroup } from 'src/app/core/models/project/assignment/comment/assignment-comment-group.interface';
import { AssignmentCommentView } from 'src/app/core/models/project/assignment/comment/assignment-comment-view.interface';
import { MinimalProfile } from 'src/app/core/models/user/profile';
import { AuthService } from 'src/app/core/services/auth.service';
import { CommentService } from 'src/app/core/services/comment.service';
import { FileService } from 'src/app/core/services/file.service';
import { Util } from 'src/app/core/utils/util';
import { TranslateService } from '@ngx-translate/core';
import { AssignmentChangeHub } from 'src/app/core/signalr-hubs/assignment-change.hub';

@Component({
    selector: 'app-assignment-comments',
    templateUrl: './comments.component.html',
    styleUrls: ['./comments.component.scss'],
    standalone: false
})
export class CommentsComponent implements OnChanges {
  private route = inject(ActivatedRoute);
  private readonly commentService = inject(CommentService);
  private readonly authService = inject(AuthService);
  private readonly translateService = inject(TranslateService);
  private readonly fileService = inject(FileService);
  private readonly assignmentChangeHub = inject(AssignmentChangeHub);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly modalService = inject(NgbModal);
  private readonly formBuilder = inject(FormBuilder);

  @Input({ required: true })
  public projectEmployees: MinimalProfile[] = [];

  @Input() isReadonly = false;

  public editingCommentId: string | null = null;
  public commentFormModel: AssignmentCommentsFormModel | null = null;
  public readonly currentLocale: string;

  public comments$ = new BehaviorSubject<AssignmentComment[]>([]);

  public groupedCommentEntries$ = this.comments$.pipe(
    map((comments) => this.buildCommentGroups(comments)),
  );

  private employeesCache: Record<string, MinimalProfile | undefined> = {};
  private assignmentId = '';

  constructor() {
    this.currentLocale = this.translateService.currentLang === 'en' ? 'en-US' : 'uk-UA';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['projectEmployees'] || !this.projectEmployees.length) {
      return;
    }

    this.route.params
      .pipe(
        first(),
        tap((params) => {
          this.assignmentId = params['id'];
          this.commentService.setCommentsUrl(this.assignmentId);
        }),
        switchMap(() => this.commentService.getAssignmentComments()),
        switchMap((comments) => {
          if (!comments.length) {
            return of(comments);
          }

          return forkJoin(this.getCommentsWithUsers$(comments)).pipe(
            map((commentsWithUsers) =>
              commentsWithUsers.map((c) => {
                c.editable =
                  c.userId === this.authService.user$.getValue().data?.id;

                return c;
              }),
            ),
            tap((initializedComments) => {
              for (const comment of initializedComments) {
                this.employeesCache[comment.userId] = comment.user ?? undefined;
              }
            }),
          );
        }),
        tap((initializedComments) => {
          this.buildAssignmentCommentsForm(initializedComments);
          this.comments$.next(initializedComments);
        }),
      )
      .subscribe();
  }

  private buildAssignmentCommentsForm(
    initializedComments: AssignmentComment[],
  ) {
    this.commentFormModel = new AssignmentCommentsFormModel(
      this.formBuilder,
      initializedComments,
    );
  }

  public addComment(): void {
    const commentFormGroup = this.commentFormModel?.form.get('newComment');
    const message = commentFormGroup?.get('message')?.value;

    if (!message) {
      return;
    }

    this.commentService
      .createComment(message)
      .pipe(
        first(),
        tap((comment) => {
          const comments = this.comments$.getValue();

          comment.sanitizedMessage = this.sanitizer.bypassSecurityTrustHtml(
            comment.message,
          );
          comment.editable = true;

          const userData = this.authService.user$.getValue().data!;

          comment.userId = userData.id;
          comment.user = this.employeesCache[comment.userId] ?? null;

          if (!comment.user) {
            comment.user = {
              id: userData.id,
              fullName: userData.fullName ?? userData.userName,
              imageUrl: userData.imageUrl ?? Constants.NotFound,
              role: userData.role,
            };
          }

          comments.push(comment);

          comments.sort(
            (a, b) =>
              new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
          );

          this.buildAssignmentCommentsForm(comments);
          this.comments$.next(comments);

          this.assignmentChangeHub.sendAssignmentChangeNotification(this.assignmentId);
        }),
      )
      .subscribe();
  }

  public editComment(id: string): void {
    this.editingCommentId = id;
  }

  public saveComment(groupIndex: number, commentIndex: number): void {
    const commentForm = this.commentFormModel?.form.controls.groupedComments
      .at(groupIndex)
      .controls.comments.at(commentIndex);

    if (!commentForm) {
      return;
    }

    const commentId = commentForm.value.id!;
    const message = commentForm.value.message!;

    this.commentService
      .updateComment(commentId, message)
      .pipe(
        first(),
        tap(() => {
          this.editingCommentId = null;

          const updatedComments = this.comments$.getValue().map((c) =>
            c.id === commentId
              ? {
                  ...c,
                  message: message,
                  sanitizedMessage:
                    this.sanitizer.bypassSecurityTrustHtml(message),
                }
              : c,
          );

          this.comments$.next(updatedComments);
        }),
      )
      .subscribe();
  }

  public cancelEditing(groupIndex: number, commentIndex: number): void {
    const commentForm = this.commentFormModel?.form.controls.groupedComments
      .at(groupIndex)
      .controls.comments.at(commentIndex);

    if (!commentForm) {
      return;
    }

    const previousMessage = this.comments$
      .getValue()
      .find((c) => c.id === this.editingCommentId)!.message;

    commentForm.patchValue({ message: previousMessage });
    this.editingCommentId = null;
  }

  public deleteComment(id: string): void {
    const modalRef = this.modalService.open(ConfirmModalComponent, {
      size: 'sm',
    });

    modalRef.componentInstance.message = Util.translateWithParam(
          this.translateService,
          `WARNINGS.DELETE`,
          {
            uk: 'коментар',
            en: 'comment',
          },
        );

    modalRef.closed
      .pipe(
        first(),
        switchMap(() => this.commentService.deleteComment(id)),
        tap(() => {
          const comments = this.comments$.getValue().filter((c) => c.id !== id);

          this.buildAssignmentCommentsForm(comments);
          this.comments$.next(comments);
        }),
      )
      .subscribe();
  }

  private getCommentsWithUsers$(
    comments: AssignmentComment[],
  ): Observable<AssignmentComment>[] {
    return comments.map((comment) => {
      comment.sanitizedMessage = this.sanitizer.bypassSecurityTrustHtml(
        comment.message,
      );

      let employee = this.employeesCache[comment.userId];

      if (!employee) {
        employee = this.projectEmployees.find((pe) => pe.id === comment.userId);
      }

      if (employee) {
        return this.fileService
          .getEntityWithImageUrl(
            employee,
            `users/${employee.id}/avatar${employee.avatarImageExtension}`,
          )
          .pipe(
            map((employee) => {
              comment.user = employee;
              return comment;
            }),
          );
      }

      return of(comment);
    });
  }

  private buildCommentGroups(
    comments: AssignmentComment[],
  ): AssignmentCommentGroup[] {
    return Object.entries(Util.groupCommentsByDate(comments))
      .sort(
        ([leftDate], [rightDate]) =>
          new Date(rightDate).getTime() - new Date(leftDate).getTime(),
      )
      .map(([dateKey, groupedComments]) => ({
        dateKey,
        displayDate: this.formatCommentGroupDate(dateKey),
        comments: groupedComments.map((comment) => this.mapCommentView(comment)),
      }));
  }

  private formatCommentGroupDate(dateKey: string): string {
    const formattedDate = new Intl.DateTimeFormat(this.currentLocale, {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    }).format(new Date(dateKey));

    return formattedDate.charAt(0).toUpperCase() + formattedDate.slice(1);
  }

  private formatCommentCreatedAt(createdAt: Date): string {
    return new Intl.DateTimeFormat(this.currentLocale, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(createdAt));
  }

  private mapCommentView(comment: AssignmentComment): AssignmentCommentView {
    return {
      ...comment,
      displayCreatedAt: this.formatCommentCreatedAt(comment.createdAt),
    };
  }
}
